import React, { useState, useEffect, useRef, useCallback } from 'react';
import { QRCodeSVG } from 'qrcode.react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Badge } from './ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from './ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Progress } from './ui/progress';
import { Textarea } from './ui/textarea';
import { useToast } from '../hooks/use-toast';
import { apiCall } from '../utils/api';
import { WsafeReviewStatus } from './WsafeReviewStatus';
import { syncManager } from '../utils/syncManager';
import ScanMapView from './ScanMapView';
import TranscriptionArchive from './TranscriptionArchive';
import HazardTrendAnalysis from './HazardTrendAnalysis';
import { 
  Camera, Upload, Scan, AlertTriangle, CheckCircle, Shield,
  Zap, Eye, FileText, Clock, RefreshCw, Info, Download, Mail, Share2, X, Copy, ClipboardCheck, Link, ExternalLink, QrCode, WifiOff, Wifi, CloudOff, Cloud, MapPin, Mic, MicOff, RotateCcw, Maximize2, Play, Pause, Trash2, Map, FileAudio, BarChart3, Search
} from 'lucide-react';

/**
 * Retry function with exponential backoff
 * @param {Function} fn - Async function to retry
 * @param {number} maxRetries - Maximum number of retries (default: 3)
 * @param {number} baseDelay - Base delay in ms (default: 1000)
 * @returns {Promise} - Result of the function or null if all retries failed
 */
const retryWithBackoff = async (fn, maxRetries = 3, baseDelay = 1000) => {
  for (let attempt = 0; attempt <= maxRetries; attempt++) {
    try {
      return await fn();
    } catch (error) {
      if (attempt === maxRetries) {
        console.warn(`All ${maxRetries + 1} attempts failed:`, error.message);
        return null;
      }
      const delay = baseDelay * Math.pow(2, attempt);
      console.log(`Attempt ${attempt + 1} failed, retrying in ${delay}ms...`);
      await new Promise(resolve => setTimeout(resolve, delay));
    }
  }
  return null;
};

const AIHazardScanPage = () => {
  const [scans, setScans] = useState([]);
  const [scanTypes, setScanTypes] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [dataError, setDataError] = useState(null);
  const [scanning, setScanning] = useState(false);
  const [selectedScan, setSelectedScan] = useState(null);
  const [previewImage, setPreviewImage] = useState(null);
  const [imageBase64, setImageBase64] = useState(null);
  const [showEmailDialog, setShowEmailDialog] = useState(false);
  const [emailData, setEmailData] = useState({ recipients: '', message: '', includeImage: true });
  const [sendingEmail, setSendingEmail] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const [copied, setCopied] = useState(false);
  
  // Share link state
  const [showShareDialog, setShowShareDialog] = useState(false);
  const [shareLink, setShareLink] = useState(null);
  const [generatingLink, setGeneratingLink] = useState(false);
  const [linkCopied, setLinkCopied] = useState(false);
  
  // Batch upload state
  const [imageQueue, setImageQueue] = useState([]); // Array of { id, file, preview, base64, status: 'pending'|'scanning'|'complete'|'error', result }
  const [batchMode, setBatchMode] = useState(false);
  const [currentBatchIndex, setCurrentBatchIndex] = useState(0);
  const [batchScanning, setBatchScanning] = useState(false);
  
  // Offline state
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const [pendingScans, setPendingScans] = useState([]);
  const [syncStatus, setSyncStatus] = useState({ pending: 0, syncing: false });
  
  // Geolocation state
  const [geoLocation, setGeoLocation] = useState(null);
  const [geoLoading, setGeoLoading] = useState(false);
  const [geoError, setGeoError] = useState(null);
  
  // Voice note state
  const [isRecording, setIsRecording] = useState(false);
  const [voiceNote, setVoiceNote] = useState(null);
  const [voiceDuration, setVoiceDuration] = useState(0);
  const [isPlayingVoice, setIsPlayingVoice] = useState(false);
  const [voiceTranscription, setVoiceTranscription] = useState(null);
  const [isTranscribing, setIsTranscribing] = useState(false);
  const mediaRecorderRef = useRef(null);
  const audioChunksRef = useRef([]);
  const audioPlayerRef = useRef(null);
  const recordingIntervalRef = useRef(null);
  
  // Pending scan preview state
  const [previewPendingScan, setPreviewPendingScan] = useState(null);
  
  // Map view state
  const [showMapView, setShowMapView] = useState(false);
  
  // Transcription archive state
  const [showTranscriptionArchive, setShowTranscriptionArchive] = useState(false);
  
  // Trend analysis state
  const [showTrendAnalysis, setShowTrendAnalysis] = useState(false);
  
  // Batch processing state
  const [batchProcessing, setBatchProcessing] = useState(false);
  const [batchProgress, setBatchProgress] = useState({ current: 0, total: 0 });
  
  const fileInputRef = useRef(null);
  const cameraInputRef = useRef(null);
  const dropZoneRef = useRef(null);
  const { toast } = useToast();

  const [scanConfig, setScanConfig] = useState({
    scan_type: 'general',
    location: '',
    project_name: ''
  });

  // Subscribe to sync manager events
  useEffect(() => {
    const handleSyncEvent = async (event) => {
      if (event.type === 'online') {
        setIsOnline(true);
        toast({ title: 'Back Online', description: 'Syncing pending scans...' });
      } else if (event.type === 'offline') {
        setIsOnline(false);
        toast({ title: 'Offline Mode', description: 'Scans will be queued and synced when back online.', variant: 'destructive' });
      } else if (event.type === 'sync_start') {
        setSyncStatus(prev => ({ ...prev, syncing: true }));
      } else if (event.type === 'sync_complete') {
        setSyncStatus({ pending: event.remaining, syncing: false });
        if (event.successCount > 0) {
          toast({ title: 'Sync Complete', description: `${event.successCount} scan(s) uploaded successfully!` });
          fetchData();
        }
      } else if (event.type === 'ai_scan_queued') {
        await loadPendingScans();
        toast({ title: 'Scan Queued', description: 'Scan saved offline. Will upload when back online.' });
      } else if (event.type === 'ai_scan_updated') {
        await loadPendingScans();
      }
    };
    
    const unsubscribe = syncManager.subscribe(handleSyncEvent);
    
    // Listen for browser online/offline events
    const handleOnline = () => setIsOnline(true);
    const handleOffline = () => setIsOnline(false);
    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);
    
    return () => {
      unsubscribe();
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, [toast]);

  // Geolocation functions
  const getCurrentLocation = useCallback(() => {
    if (!navigator.geolocation) {
      setGeoError('Geolocation not supported by your browser');
      return;
    }
    
    setGeoLoading(true);
    setGeoError(null);
    
    navigator.geolocation.getCurrentPosition(
      (position) => {
        setGeoLocation({
          latitude: position.coords.latitude,
          longitude: position.coords.longitude,
          accuracy: position.coords.accuracy,
          timestamp: new Date().toISOString()
        });
        setGeoLoading(false);
        toast({ title: 'Location Captured', description: 'GPS coordinates added to scan' });
      },
      (error) => {
        setGeoLoading(false);
        let message = 'Could not get location';
        if (error.code === 1) message = 'Location permission denied';
        else if (error.code === 2) message = 'Location unavailable';
        else if (error.code === 3) message = 'Location request timed out';
        setGeoError(message);
        toast({ title: 'Location Error', description: message, variant: 'destructive' });
      },
      { enableHighAccuracy: true, timeout: 10000, maximumAge: 60000 }
    );
  }, [toast]);

  // Voice recording functions
  const startRecording = useCallback(async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      mediaRecorderRef.current = new MediaRecorder(stream);
      audioChunksRef.current = [];
      
      mediaRecorderRef.current.ondataavailable = (event) => {
        if (event.data.size > 0) {
          audioChunksRef.current.push(event.data);
        }
      };
      
      mediaRecorderRef.current.onstop = () => {
        const audioBlob = new Blob(audioChunksRef.current, { type: 'audio/webm' });
        const reader = new FileReader();
        reader.onloadend = () => {
          setVoiceNote({
            base64: reader.result,
            duration: voiceDuration,
            timestamp: new Date().toISOString()
          });
        };
        reader.readAsDataURL(audioBlob);
        
        // Stop all tracks
        stream.getTracks().forEach(track => track.stop());
      };
      
      mediaRecorderRef.current.start();
      setIsRecording(true);
      setVoiceDuration(0);
      
      // Duration counter
      recordingIntervalRef.current = setInterval(() => {
        setVoiceDuration(prev => prev + 1);
      }, 1000);
      
      toast({ title: 'Recording Started', description: 'Speak to add a voice note' });
    } catch (error) {
      console.error('Recording error:', error);
      toast({ title: 'Microphone Error', description: 'Could not access microphone', variant: 'destructive' });
    }
  }, [voiceDuration, toast]);

  const stopRecording = useCallback(() => {
    if (mediaRecorderRef.current && isRecording) {
      mediaRecorderRef.current.stop();
      setIsRecording(false);
      if (recordingIntervalRef.current) {
        clearInterval(recordingIntervalRef.current);
      }
      toast({ title: 'Recording Saved', description: `${voiceDuration}s voice note attached` });
    }
  }, [isRecording, voiceDuration, toast]);

  const playVoiceNote = useCallback(() => {
    if (voiceNote?.base64 && audioPlayerRef.current) {
      audioPlayerRef.current.src = voiceNote.base64;
      audioPlayerRef.current.play();
      setIsPlayingVoice(true);
    }
  }, [voiceNote]);

  const stopPlayingVoice = useCallback(() => {
    if (audioPlayerRef.current) {
      audioPlayerRef.current.pause();
      audioPlayerRef.current.currentTime = 0;
      setIsPlayingVoice(false);
    }
  }, []);

  // Load pending offline scans - defined first as it's used by other callbacks
  const loadPendingScans = useCallback(async () => {
    try {
      const pending = await syncManager.getPendingAIScans();
      setPendingScans(pending || []);
      setSyncStatus(prev => ({ ...prev, pending: pending?.length || 0 }));
    } catch (e) {
      console.warn('Failed to load pending scans:', e);
    }
  }, []);

  const deleteVoiceNote = useCallback(() => {
    setVoiceNote(null);
    setVoiceDuration(0);
    setVoiceTranscription(null);
    toast({ title: 'Voice Note Removed' });
  }, [toast]);

  // Transcribe voice note using Whisper
  const transcribeVoiceNote = useCallback(async () => {
    if (!voiceNote?.base64) return;
    
    setIsTranscribing(true);
    try {
      const data = await apiCall('/api/ai-hazard-scan/transcribe', {
        method: 'POST',
        body: {
          audio_base64: voiceNote.base64,
          language: 'en'
        }
      });
      
      if (data.success && data.text) {
        setVoiceTranscription(data.text);
        toast({ title: 'Transcription Complete', description: 'Voice note converted to text' });
      }
    } catch (error) {
      console.error('Transcription error:', error);
      toast({ title: 'Transcription Failed', description: error.message, variant: 'destructive' });
    } finally {
      setIsTranscribing(false);
    }
  }, [voiceNote, toast]);

  // Batch process all pending scans
  const processBatchOfflineScans = useCallback(async () => {
    if (!isOnline || pendingScans.length === 0) {
      toast({ title: 'Cannot Process', description: 'Need to be online with pending scans', variant: 'destructive' });
      return;
    }
    
    setBatchProcessing(true);
    setBatchProgress({ current: 0, total: pendingScans.length });
    
    try {
      // Prepare batch request
      const scansToProcess = pendingScans.map(scan => ({
        id: scan.id,
        image_base64: scan.image_base64,
        scan_type: scan.scan_type || 'general',
        location: scan.location,
        project_name: scan.project_name,
        geolocation: scan.geolocation,
        voice_note: scan.voice_note
      }));
      
      toast({ title: 'Processing Batch', description: `Analyzing ${scansToProcess.length} scans...` });
      
      const result = await apiCall('/api/ai-hazard-scan/batch-analyze', {
        method: 'POST',
        body: { scans: scansToProcess }
      });
      
      // Update local status for processed scans
      for (const scanResult of result.results) {
        if (scanResult.success) {
          await syncManager.updateAIScanStatus(scanResult.offline_id, 'synced', scanResult);
        } else {
          await syncManager.updateAIScanStatus(scanResult.offline_id, 'failed');
        }
      }
      
      await loadPendingScans();
      // Trigger a refresh of the main data
      window.dispatchEvent(new CustomEvent('refresh-scan-data'));
      
      toast({ 
        title: 'Batch Complete', 
        description: `${result.successful} of ${result.processed} scans processed successfully` 
      });
    } catch (error) {
      console.error('Batch processing error:', error);
      toast({ title: 'Batch Failed', description: error.message, variant: 'destructive' });
    } finally {
      setBatchProcessing(false);
      setBatchProgress({ current: 0, total: 0 });
    }
  }, [isOnline, pendingScans, loadPendingScans, toast]);

  // Retry failed scan
  const retryFailedScan = useCallback(async (scanId) => {
    const scan = pendingScans.find(s => s.id === scanId);
    if (!scan) return;
    
    if (!isOnline) {
      toast({ title: 'Offline', description: 'Cannot retry while offline', variant: 'destructive' });
      return;
    }
    
    try {
      await syncManager.updateAIScanStatus(scanId, 'pending');
      await loadPendingScans();
      toast({ title: 'Retrying', description: 'Scan queued for retry' });
      setTimeout(() => syncManager.syncAll(), 500);
    } catch (error) {
      toast({ title: 'Retry Failed', description: error.message, variant: 'destructive' });
    }
  }, [pendingScans, isOnline, loadPendingScans, toast]);

  // Delete pending scan
  const deletePendingScan = useCallback(async (scanId) => {
    try {
      await syncManager.removeAIScan(scanId);
      await loadPendingScans();
      toast({ title: 'Scan Removed', description: 'Pending scan deleted' });
    } catch (error) {
      toast({ title: 'Delete Failed', description: error.message, variant: 'destructive' });
    }
  }, [loadPendingScans, toast]);

  // Format duration as MM:SS
  const formatDuration = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  // fetchData defined first so it can be used by other callbacks
  const fetchData = useCallback(async () => {
    setLoading(true);
    setDataError(null);
    
    try {
      // If offline, try to use cached data
      if (!navigator.onLine) {
        const cached = await syncManager.fetchWithCache('/api/ai-hazard-scan/stats').catch(() => null);
        if (cached?.data) {
          setStats(cached.data);
        }
        setLoading(false);
        return;
      }

      // Use retry with exponential backoff for resilience on slow networks
      const [scansData, typesData, statsData] = await Promise.all([
        retryWithBackoff(() => apiCall('/api/ai-hazard-scan/scans'), 3, 500),
        retryWithBackoff(() => apiCall('/api/ai-hazard-scan/scan-types'), 3, 500),
        retryWithBackoff(() => apiCall('/api/ai-hazard-scan/stats'), 3, 500)
      ]);
      
      if (scansData?.scans) setScans(scansData.scans);
      if (typesData?.scan_types) setScanTypes(typesData.scan_types);
      if (statsData) {
        setStats(statsData);
        // Cache stats for offline use
        await syncManager.cacheAPIResponse('/api/ai-hazard-scan/stats', statsData, 30);
      }
      
      // Check if we got any data at all - only show error if all calls truly failed
      if (!scansData && !typesData && !statsData && navigator.onLine) {
        setDataError('Could not load data. Please check your connection.');
        toast({ 
          title: 'Load Failed', 
          description: 'Could not fetch data after multiple retries. Click Retry to try again.', 
          variant: 'destructive' 
        });
      }
    } catch (error) {
      console.error('FetchData error:', error);
      setDataError(error.message);
      toast({ title: 'Error', description: error.message, variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  }, [toast]);

  useEffect(() => {
    fetchData();
    loadPendingScans();
    
    // Listen for refresh events from batch processing
    const handleRefresh = () => {
      fetchData();
    };
    window.addEventListener('refresh-scan-data', handleRefresh);
    
    return () => {
      window.removeEventListener('refresh-scan-data', handleRefresh);
    };
  }, [fetchData, loadPendingScans]);

  // Download scan report
  const handleDownload = async (format = 'html') => {
    if (!selectedScan) {
      toast({ title: 'Error', description: 'No scan selected', variant: 'destructive' });
      return;
    }
    
    const scanId = selectedScan.id || selectedScan.scan_id;
    if (!scanId) {
      toast({ title: 'Error', description: 'Cannot download - scan ID not found', variant: 'destructive' });
      return;
    }
    
    try {
      toast({ title: 'Downloading...', description: 'Preparing your report' });
      
      const data = await apiCall(`/api/ai-hazard-scan/scans/${scanId}/download?format=${format}`);
      const { content, filename, content_type } = data;
      
      if (!content) {
        throw new Error('No content received from server');
      }
      
      // Create blob
      let blob;
      if (format === 'json') {
        blob = new Blob([JSON.stringify(content, null, 2)], { type: 'application/json;charset=utf-8' });
      } else {
        blob = new Blob([content], { type: `${content_type || 'text/html'};charset=utf-8` });
      }
      
      // Download file - Edge compatible
      if (window.navigator.msSaveOrOpenBlob) {
        window.navigator.msSaveOrOpenBlob(blob, filename);
      } else {
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = filename || `scan_${scanId}.${format}`;
        document.body.appendChild(a);
        a.click();
        setTimeout(() => {
          document.body.removeChild(a);
          URL.revokeObjectURL(url);
        }, 100);
      }
      
      toast({ title: 'Success', description: `Report downloaded as ${format.toUpperCase()}` });
    } catch (error) {
      console.error('Download error:', error);
      toast({ title: 'Download Failed', description: error.message, variant: 'destructive' });
    }
  };

  // Copy scan report to clipboard
  const handleCopyToClipboard = async () => {
    if (!selectedScan) {
      toast({ title: 'Error', description: 'No scan selected', variant: 'destructive' });
      return;
    }
    
    try {
      const analysis = selectedScan.analysis || {};
      const hazards = analysis.hazards_found || [];
      
      // Build a clean text report
      let reportText = `═══════════════════════════════════════════════════════════════
                    AI HAZARD SCAN REPORT
═══════════════════════════════════════════════════════════════

SCAN DETAILS
────────────────────────────────────────────────────────────────
Scan Number:    ${selectedScan.scan_number || 'N/A'}
Location:       ${selectedScan.location || 'Unknown'}
Project:        ${selectedScan.project_name || 'N/A'}
Scan Type:      ${selectedScan.scan_type || 'General'}
Date:           ${selectedScan.created_at ? new Date(selectedScan.created_at).toLocaleString() : 'N/A'}
Overall Risk:   ${(selectedScan.overall_risk || analysis.overall_risk_level || 'Unknown').toUpperCase()}

SUMMARY
────────────────────────────────────────────────────────────────
${analysis.summary || 'No summary available.'}

`;

      if (hazards.length > 0) {
        reportText += `HAZARDS FOUND (${hazards.length})
────────────────────────────────────────────────────────────────
`;
        hazards.forEach((hazard, index) => {
          reportText += `
${index + 1}. ${hazard.hazard_type || 'Unknown Hazard'}
   Severity:    ${(hazard.severity || 'Unknown').toUpperCase()}
   Description: ${hazard.description || 'No description'}
   Location:    ${hazard.location_in_image || 'Not specified'}
   Action:      ${hazard.recommended_action || 'No action specified'}
   OSHA Ref:    ${hazard.osha_reference || 'N/A'}
`;
        });
      }

      if (analysis.immediate_actions_required?.length > 0) {
        reportText += `
IMMEDIATE ACTIONS REQUIRED
────────────────────────────────────────────────────────────────
`;
        analysis.immediate_actions_required.forEach((action, i) => {
          reportText += `• ${action}\n`;
        });
      }

      if (analysis.positive_observations?.length > 0) {
        reportText += `
POSITIVE OBSERVATIONS
────────────────────────────────────────────────────────────────
`;
        analysis.positive_observations.forEach((obs, i) => {
          reportText += `✓ ${obs}\n`;
        });
      }

      reportText += `
═══════════════════════════════════════════════════════════════
Generated by WSafe AI - AI-Powered Safety Management
═══════════════════════════════════════════════════════════════
`;

      // Try modern clipboard API first
      if (navigator.clipboard && window.isSecureContext) {
        await navigator.clipboard.writeText(reportText);
      } else {
        // Fallback for older browsers
        const textArea = document.createElement('textarea');
        textArea.value = reportText;
        textArea.style.position = 'fixed';
        textArea.style.left = '-9999px';
        textArea.style.top = '-9999px';
        document.body.appendChild(textArea);
        textArea.focus();
        textArea.select();
        document.execCommand('copy');
        document.body.removeChild(textArea);
      }
      
      setCopied(true);
      toast({ title: 'Copied!', description: 'Report copied to clipboard' });
      
      // Reset copied state after 2 seconds
      setTimeout(() => setCopied(false), 2000);
    } catch (error) {
      console.error('Copy error:', error);
      toast({ 
        title: 'Copy Failed', 
        description: 'Could not copy to clipboard. Please try selecting and copying manually.',
        variant: 'destructive' 
      });
    }
  };

  // Generate shareable link
  const handleGenerateShareLink = async () => {
    if (!selectedScan) {
      toast({ title: 'Error', description: 'No scan selected', variant: 'destructive' });
      return;
    }
    
    const scanId = selectedScan.id || selectedScan.scan_id;
    if (!scanId) {
      toast({ title: 'Error', description: 'Cannot share - scan ID not found', variant: 'destructive' });
      return;
    }
    
    setGeneratingLink(true);
    setShareLink(null);
    
    try {
      const data = await apiCall(`/api/ai-hazard-scan/scans/${scanId}/share`, {
        method: 'POST',
        body: { expires_in_days: 7 }
      });
      
      if (data.success) {
        const shareUrl = `${window.location.origin}/shared/scan/${data.share_token}`;
        setShareLink({
          url: shareUrl,
          token: data.share_token,
          expiresAt: data.expires_at
        });
        setShowShareDialog(true);
        toast({ title: 'Link Generated', description: 'Shareable link is ready!' });
      } else {
        throw new Error('Failed to generate link');
      }
    } catch (error) {
      console.error('Share link error:', error);
      toast({ title: 'Share Failed', description: error.message, variant: 'destructive' });
    } finally {
      setGeneratingLink(false);
    }
  };

  // Copy share link to clipboard
  const handleCopyShareLink = async () => {
    if (!shareLink?.url) return;
    
    try {
      if (navigator.clipboard && window.isSecureContext) {
        await navigator.clipboard.writeText(shareLink.url);
      } else {
        const textArea = document.createElement('textarea');
        textArea.value = shareLink.url;
        textArea.style.position = 'fixed';
        textArea.style.left = '-9999px';
        document.body.appendChild(textArea);
        textArea.select();
        document.execCommand('copy');
        document.body.removeChild(textArea);
      }
      
      setLinkCopied(true);
      toast({ title: 'Copied!', description: 'Link copied to clipboard' });
      setTimeout(() => setLinkCopied(false), 2000);
    } catch (error) {
      toast({ title: 'Copy Failed', description: 'Could not copy link', variant: 'destructive' });
    }
  };

  // Email scan report
  const handleSendEmail = async () => {
    if (!selectedScan || !emailData.recipients.trim()) return;
    
    const scanId = selectedScan.id || selectedScan.scan_id;
    if (!scanId) {
      toast({ title: 'Error', description: 'Cannot email - scan ID not found', variant: 'destructive' });
      return;
    }
    
    const recipients = emailData.recipients.split(',').map(e => e.trim()).filter(e => e);
    if (recipients.length === 0) {
      toast({ title: 'Error', description: 'Please enter at least one email address', variant: 'destructive' });
      return;
    }
    
    try {
      setSendingEmail(true);
      
      const data = await apiCall(`/api/ai-hazard-scan/scans/${scanId}/email`, {
        method: 'POST',
        body: {
          recipient_emails: recipients,
          message: emailData.message,
          include_image: emailData.includeImage
        }
      });
      
      if (data.success) {
        toast({ title: 'Success', description: `Report sent to ${recipients.length} recipient(s)` });
        setShowEmailDialog(false);
        setEmailData({ recipients: '', message: '', includeImage: true });
      } else {
        toast({ title: 'Warning', description: data.message || 'Email may not have been sent', variant: 'destructive' });
      }
    } catch (error) {
      toast({ title: 'Error', description: error.message || 'Failed to send email', variant: 'destructive' });
    } finally {
      setSendingEmail(false);
    }
  };

  // Validate a single file
  const validateFile = (file) => {
    const validTypes = ['image/jpeg', 'image/png', 'image/jpg', 'image/heic', 'image/heif', 'image/webp'];
    if (!validTypes.includes(file.type) && !file.name.match(/\.(jpg|jpeg|png|heic|heif|webp)$/i)) {
      return { valid: false, error: 'Invalid file type' };
    }
    if (file.size > 10 * 1024 * 1024) {
      return { valid: false, error: 'File too large (max 10MB)' };
    }
    return { valid: true };
  };

  // Process single file (for single mode)
  const processFile = (file) => {
    if (!file) return;
    
    const validation = validateFile(file);
    if (!validation.valid) {
      toast({ 
        title: 'Invalid File', 
        description: validation.error, 
        variant: 'destructive' 
      });
      return;
    }
    
    // Use canvas to normalize the image (fixes Chrome/browser-specific issues)
    const img = new Image();
    img.onload = () => {
      try {
        // Create canvas to normalize the image
        const canvas = document.createElement('canvas');
        const ctx = canvas.getContext('2d');
        
        // Set canvas size to image size (max 2048px to avoid memory issues)
        const maxSize = 2048;
        let width = img.width;
        let height = img.height;
        
        if (width > maxSize || height > maxSize) {
          if (width > height) {
            height = Math.round((height * maxSize) / width);
            width = maxSize;
          } else {
            width = Math.round((width * maxSize) / height);
            height = maxSize;
          }
        }
        
        canvas.width = width;
        canvas.height = height;
        
        // Draw image to canvas (normalizes format)
        ctx.fillStyle = '#FFFFFF';
        ctx.fillRect(0, 0, width, height);
        ctx.drawImage(img, 0, 0, width, height);
        
        // Get normalized base64 as JPEG (most compatible)
        const normalizedBase64 = canvas.toDataURL('image/jpeg', 0.9);
        
        setPreviewImage(URL.createObjectURL(file));
        setImageBase64(normalizedBase64);
        toast({ title: 'Image Ready', description: 'Click "Scan for Hazards" to analyze' });
      } catch (err) {
        console.error('Image processing error:', err);
        // Fallback to direct FileReader
        const reader = new FileReader();
        reader.onloadend = () => {
          setPreviewImage(URL.createObjectURL(file));
          setImageBase64(reader.result);
          toast({ title: 'Image Ready', description: 'Click "Scan for Hazards" to analyze' });
        };
        reader.readAsDataURL(file);
      }
    };
    
    img.onerror = () => {
      toast({ 
        title: 'Error', 
        description: 'Failed to load image. Please try a different file.', 
        variant: 'destructive' 
      });
    };
    
    // Load image from file
    img.src = URL.createObjectURL(file);
  };

  // Process multiple files for batch mode
  const processMultipleFiles = async (files) => {
    const fileArray = Array.from(files);
    const validFiles = [];
    let invalidCount = 0;
    
    for (const file of fileArray) {
      const validation = validateFile(file);
      if (validation.valid) {
        validFiles.push(file);
      } else {
        invalidCount++;
      }
    }
    
    if (invalidCount > 0) {
      toast({ 
        title: 'Some Files Skipped', 
        description: `${invalidCount} file(s) were invalid and skipped`, 
        variant: 'destructive' 
      });
    }
    
    if (validFiles.length === 0) return;
    
    // If multiple files, switch to batch mode
    if (validFiles.length > 1 || imageQueue.length > 0) {
      setBatchMode(true);
    }
    
    // Helper function to normalize image via canvas
    const normalizeImage = (file) => {
      return new Promise((resolve, reject) => {
        const img = new Image();
        img.onload = () => {
          try {
            const canvas = document.createElement('canvas');
            const ctx = canvas.getContext('2d');
            
            const maxSize = 2048;
            let width = img.width;
            let height = img.height;
            
            if (width > maxSize || height > maxSize) {
              if (width > height) {
                height = Math.round((height * maxSize) / width);
                width = maxSize;
              } else {
                width = Math.round((width * maxSize) / height);
                height = maxSize;
              }
            }
            
            canvas.width = width;
            canvas.height = height;
            ctx.fillStyle = '#FFFFFF';
            ctx.fillRect(0, 0, width, height);
            ctx.drawImage(img, 0, 0, width, height);
            
            resolve(canvas.toDataURL('image/jpeg', 0.9));
          } catch (err) {
            reject(err);
          }
        };
        img.onerror = reject;
        img.src = URL.createObjectURL(file);
      });
    };
    
    // Process each valid file with canvas normalization
    const newQueueItems = [];
    for (const file of validFiles) {
      try {
        const normalizedBase64 = await normalizeImage(file);
        newQueueItems.push({
          id: `${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
          file,
          name: file.name,
          preview: URL.createObjectURL(file),
          base64: normalizedBase64,
          status: 'pending',
          result: null
        });
      } catch (err) {
        // Fallback to FileReader if canvas fails
        const base64 = await new Promise((resolve) => {
          const reader = new FileReader();
          reader.onloadend = () => resolve(reader.result);
          reader.readAsDataURL(file);
        });
        newQueueItems.push({
          id: `${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
          file,
          name: file.name,
          preview: URL.createObjectURL(file),
          base64,
          status: 'pending',
          result: null
        });
      }
    }
    
    setImageQueue(prev => [...prev, ...newQueueItems]);
    
    const totalCount = imageQueue.length + newQueueItems.length;
    toast({ 
      title: 'Images Added to Queue', 
      description: `${newQueueItems.length} image(s) added. Total: ${totalCount} in queue.` 
    });
  };

  const handleImageSelect = (e) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;
    
    if (files.length === 1 && !batchMode && imageQueue.length === 0) {
      // Single file, single mode
      processFile(files[0]);
    } else {
      // Multiple files or already in batch mode
      processMultipleFiles(files);
    }
    
    // Reset input
    e.target.value = '';
  };

  // Remove image from queue
  const removeFromQueue = (id) => {
    setImageQueue(prev => {
      const updated = prev.filter(item => item.id !== id);
      if (updated.length === 0) {
        setBatchMode(false);
      }
      return updated;
    });
  };

  // Clear entire queue
  const clearQueue = () => {
    setImageQueue([]);
    setBatchMode(false);
    setCurrentBatchIndex(0);
  };

  // Process batch scan
  const processBatchScan = async () => {
    if (imageQueue.length === 0) return;
    
    setBatchScanning(true);
    setCurrentBatchIndex(0);
    
    for (let i = 0; i < imageQueue.length; i++) {
      const item = imageQueue[i];
      if (item.status !== 'pending') continue;
      
      setCurrentBatchIndex(i);
      setImageQueue(prev => prev.map((img, idx) => 
        idx === i ? { ...img, status: 'scanning' } : img
      ));
      
      try {
        const data = await apiCall('/api/ai-hazard-scan/analyze', {
          method: 'POST',
          body: {
            image_base64: item.base64,
            scan_type: scanConfig.scan_type,
            location: scanConfig.location || `Batch Scan - Image ${i + 1}`,
            project_name: scanConfig.project_name || 'Batch Analysis'
          }
        });
        
        setImageQueue(prev => prev.map((img, idx) => 
          idx === i ? { ...img, status: 'complete', result: data } : img
        ));
      } catch (error) {
        setImageQueue(prev => prev.map((img, idx) => 
          idx === i ? { ...img, status: 'error', result: { error: error.message } } : img
        ));
      }
    }
    
    setBatchScanning(false);
    fetchData();
    
    const completedCount = imageQueue.filter(img => img.status === 'complete' || img.status === 'scanning').length;
    toast({ 
      title: 'Batch Scan Complete', 
      description: `Analyzed ${completedCount} image(s).` 
    });
  };

  // Handle batch export
  const handleBatchExport = async (format) => {
    const completedScans = imageQueue.filter(i => i.status === 'complete' && i.result?.scan_id);
    if (completedScans.length === 0) {
      toast({ title: 'No Scans', description: 'No completed scans to export', variant: 'destructive' });
      return;
    }
    
    try {
      toast({ title: 'Generating Report', description: 'Please wait...' });
      
      const data = await apiCall('/api/ai-hazard-scan/batch-export', {
        method: 'POST',
        body: {
          scan_ids: completedScans.map(s => s.result.scan_id),
          format: format,
          project_name: scanConfig.project_name || 'Batch Inspection',
          location: scanConfig.location || 'Multiple Locations'
        }
      });
      
      const { content, filename, content_type } = data;
      
      // Create blob
      let blob;
      if (format === 'json') {
        blob = new Blob([JSON.stringify(content, null, 2)], { type: 'application/json;charset=utf-8' });
      } else {
        blob = new Blob([content], { type: `${content_type};charset=utf-8` });
      }
      
      // Download - Edge compatible
      if (window.navigator.msSaveOrOpenBlob) {
        window.navigator.msSaveOrOpenBlob(blob, filename);
      } else {
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = filename;
        document.body.appendChild(a);
        a.click();
        setTimeout(() => {
          document.body.removeChild(a);
          URL.revokeObjectURL(url);
        }, 100);
      }
      
      toast({ title: 'Export Complete', description: `Downloaded ${filename}` });
    } catch (error) {
      toast({ title: 'Export Failed', description: error.message, variant: 'destructive' });
    }
  };

  // Drag and drop handlers
  const handleDragEnter = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.currentTarget === dropZoneRef.current && !e.currentTarget.contains(e.relatedTarget)) {
      setIsDragging(false);
    }
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
    
    const files = e.dataTransfer?.files;
    if (files && files.length > 0) {
      if (files.length === 1 && !batchMode && imageQueue.length === 0) {
        processFile(files[0]);
      } else {
        processMultipleFiles(files);
      }
    }
  };

  const handleScan = async () => {
    if (!imageBase64) {
      toast({ title: 'No Image', description: 'Please select an image first', variant: 'destructive' });
      return;
    }

    setScanning(true);
    
    const scanData = {
      image_base64: imageBase64,
      scan_type: scanConfig.scan_type,
      location: scanConfig.location || 'Unknown Location',
      project_name: scanConfig.project_name || 'Unnamed Project',
      // Include geolocation if available
      geolocation: geoLocation ? {
        latitude: geoLocation.latitude,
        longitude: geoLocation.longitude,
        accuracy: geoLocation.accuracy,
        timestamp: geoLocation.timestamp
      } : null,
      // Include voice note if available
      voice_note: voiceNote ? {
        audio_base64: voiceNote.base64,
        duration: voiceNote.duration,
        timestamp: voiceNote.timestamp
      } : null
    };

    // Reset geolocation and voice note for next scan
    const resetExtras = () => {
      setGeoLocation(null);
      setVoiceNote(null);
      setVoiceDuration(0);
    };

    // If offline, save locally and queue for sync
    if (!isOnline) {
      try {
        const offlineScan = await syncManager.saveAIScanOffline(scanData);
        
        toast({ 
          title: 'Scan Saved Offline', 
          description: 'Your scan has been saved and will be analyzed when you\'re back online.' 
        });
        
        setPreviewImage(null);
        setImageBase64(null);
        setScanConfig({ scan_type: 'general', location: '', project_name: '' });
        resetExtras();
        
        await loadPendingScans();
      } catch (error) {
        console.error('Offline save error:', error);
        toast({ title: 'Save Failed', description: 'Could not save scan offline.', variant: 'destructive' });
      } finally {
        setScanning(false);
      }
      return;
    }

    // Online - perform scan immediately
    try {
      toast({ title: 'Analyzing...', description: 'AI is scanning your image for hazards' });
      
      const data = await apiCall('/api/ai-hazard-scan/analyze', {
        method: 'POST',
        body: scanData
      });

      toast({ 
        title: 'Scan Complete!', 
        description: `Found ${data.hazards_count} potential hazards` 
      });
      
      setSelectedScan({
        ...data,
        created_at: new Date().toISOString()
      });
      
      setPreviewImage(null);
      setImageBase64(null);
      setScanConfig({ scan_type: 'general', location: '', project_name: '' });
      resetExtras();
      
      fetchData();
    } catch (error) {
      console.error('Scan error:', error);
      
      // If network error, try to save offline
      if (error.message.includes('Failed to fetch') || error.message.includes('NetworkError')) {
        try {
          await syncManager.saveAIScanOffline(scanData);
          toast({ 
            title: 'Network Error - Saved Offline', 
            description: 'Could not reach server. Scan saved for later upload.' 
          });
          setPreviewImage(null);
          setImageBase64(null);
          setScanConfig({ scan_type: 'general', location: '', project_name: '' });
          await loadPendingScans();
        } catch (offlineError) {
          toast({ title: 'Scan Failed', description: 'Could not save scan.', variant: 'destructive' });
        }
      } else {
        toast({ title: 'Scan Failed', description: error.message || 'AI analysis failed', variant: 'destructive' });
      }
    } finally {
      setScanning(false);
    }
  };

  const getRiskColor = (risk) => {
    switch (risk) {
      case 'critical': return 'bg-red-100 text-red-800 border-red-300';
      case 'high': return 'bg-orange-100 text-orange-800 border-orange-300';
      case 'medium': return 'bg-yellow-100 text-yellow-800 border-yellow-300';
      case 'low': return 'bg-green-100 text-green-800 border-green-300';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getSeverityIcon = (severity) => {
    switch (severity) {
      case 'critical': return <AlertTriangle className="w-5 h-5 text-red-600" />;
      case 'high': return <AlertTriangle className="w-5 h-5 text-orange-500" />;
      case 'medium': return <Info className="w-5 h-5 text-yellow-500" />;
      case 'low': return <CheckCircle className="w-5 h-5 text-green-500" />;
      default: return <Info className="w-5 h-5 text-gray-400" />;
    }
  };

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center h-64 space-y-4">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
        <p className="text-gray-500">Loading scan data...</p>
      </div>
    );
  }

  // Show retry button if no data loaded
  if (!stats && scans.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center h-64 space-y-4">
        <AlertTriangle className="w-12 h-12 text-orange-500" />
        <p className="text-gray-600">Could not load data</p>
        <Button onClick={fetchData} variant="outline">
          <RefreshCw className="w-4 h-4 mr-2" />
          Retry
        </Button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Offline Status Banner */}
      {!isOnline && (
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 flex items-center gap-3">
          <WifiOff className="w-5 h-5 text-amber-600 flex-shrink-0" />
          <div className="flex-1">
            <p className="font-medium text-amber-800">Offline Mode</p>
            <p className="text-sm text-amber-600">Scans will be saved locally and uploaded when you're back online.</p>
          </div>
          <CloudOff className="w-5 h-5 text-amber-400" />
        </div>
      )}

      {/* Pending Scans Banner */}
      {pendingScans.length > 0 && (
        <div className={`${isOnline ? 'bg-blue-50 border-blue-200' : 'bg-gray-50 border-gray-200'} border rounded-lg p-4`}>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              {syncStatus.syncing ? (
                <RefreshCw className="w-5 h-5 text-blue-600 animate-spin" />
              ) : (
                <Cloud className="w-5 h-5 text-blue-600" />
              )}
              <div>
                <p className="font-medium text-gray-800">
                  {syncStatus.syncing 
                    ? 'Syncing pending scans...' 
                    : `${pendingScans.length} scan(s) waiting to upload`}
                </p>
                <p className="text-sm text-gray-600">
                  {isOnline 
                    ? 'These will be processed automatically' 
                    : 'Will sync when connection is restored'}
                </p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              {isOnline && !syncStatus.syncing && !batchProcessing && (
                <>
                  <Button 
                    size="sm" 
                    variant="outline"
                    onClick={() => syncManager.syncAll()}
                    className="flex items-center gap-2"
                  >
                    <RefreshCw className="w-4 h-4" />
                    Sync Now
                  </Button>
                  <Button 
                    size="sm" 
                    variant="default"
                    onClick={processBatchOfflineScans}
                    className="flex items-center gap-2 bg-purple-600 hover:bg-purple-700"
                    data-testid="batch-process-btn"
                  >
                    <Zap className="w-4 h-4" />
                    Batch Process All
                  </Button>
                </>
              )}
              {batchProcessing && (
                <div className="flex items-center gap-2 text-purple-600">
                  <RefreshCw className="w-4 h-4 animate-spin" />
                  <span className="text-sm">Processing batch...</span>
                </div>
              )}
            </div>
          </div>
          
          {/* Pending scans list */}
          <div className="mt-3 space-y-2">
            {pendingScans.slice(0, 5).map((scan) => (
              <div key={scan.id} className="flex items-center justify-between bg-white rounded-lg p-3 border">
                <div className="flex items-center gap-3 flex-1">
                  {/* Thumbnail with preview button */}
                  <button 
                    onClick={() => setPreviewPendingScan(scan)}
                    className="relative w-12 h-12 bg-gray-100 rounded overflow-hidden group flex-shrink-0"
                  >
                    {scan.image_base64 && (
                      <img 
                        src={scan.image_base64} 
                        alt="Pending scan" 
                        className="w-full h-full object-cover"
                      />
                    )}
                    <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 flex items-center justify-center transition-opacity">
                      <Maximize2 className="w-4 h-4 text-white" />
                    </div>
                  </button>
                  
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-gray-700 truncate">{scan.location || 'Unknown Location'}</p>
                    <div className="flex items-center gap-2 text-xs text-gray-500">
                      <span>{scan.scan_type} scan</span>
                      {scan.geolocation && (
                        <span className="flex items-center gap-1 text-green-600">
                          <MapPin className="w-3 h-3" />
                          GPS
                        </span>
                      )}
                      {scan.voice_note && (
                        <span className="flex items-center gap-1 text-purple-600">
                          <Mic className="w-3 h-3" />
                          {formatDuration(scan.voice_note.duration)}
                        </span>
                      )}
                    </div>
                  </div>
                </div>
                
                <div className="flex items-center gap-2">
                  <Badge variant="secondary" className={
                    scan.status === 'syncing' ? 'bg-blue-100 text-blue-700' :
                    scan.status === 'failed' ? 'bg-red-100 text-red-700' :
                    'bg-amber-100 text-amber-700'
                  }>
                    {scan.status === 'syncing' ? 'Syncing' : scan.status === 'failed' ? 'Failed' : 'Pending'}
                  </Badge>
                  
                  {/* Retry button for failed scans */}
                  {scan.status === 'failed' && isOnline && (
                    <Button 
                      size="sm" 
                      variant="ghost" 
                      onClick={() => retryFailedScan(scan.id)}
                      className="h-8 w-8 p-0 text-blue-600 hover:text-blue-700 hover:bg-blue-50"
                      title="Retry sync"
                    >
                      <RotateCcw className="w-4 h-4" />
                    </Button>
                  )}
                  
                  {/* Delete button */}
                  <Button 
                    size="sm" 
                    variant="ghost" 
                    onClick={() => deletePendingScan(scan.id)}
                    className="h-8 w-8 p-0 text-red-600 hover:text-red-700 hover:bg-red-50"
                    title="Delete scan"
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            ))}
            {pendingScans.length > 5 && (
              <p className="text-xs text-gray-500 text-center">
                +{pendingScans.length - 5} more pending scan(s)
              </p>
            )}
          </div>
        </div>
      )}

      {/* Pending Scan Preview Dialog */}
      <Dialog open={!!previewPendingScan} onOpenChange={() => setPreviewPendingScan(null)}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Eye className="w-5 h-5" />
              Pending Scan Preview
            </DialogTitle>
            <DialogDescription>
              This scan is queued for upload
            </DialogDescription>
          </DialogHeader>
          
          {previewPendingScan && (
            <div className="space-y-4">
              {/* Image preview */}
              <div className="rounded-lg overflow-hidden bg-gray-100">
                <img 
                  src={previewPendingScan.image_base64} 
                  alt="Scan preview" 
                  className="w-full h-auto max-h-[400px] object-contain"
                />
              </div>
              
              {/* Scan details */}
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <p className="text-gray-500">Location</p>
                  <p className="font-medium">{previewPendingScan.location || 'Unknown'}</p>
                </div>
                <div>
                  <p className="text-gray-500">Scan Type</p>
                  <p className="font-medium capitalize">{previewPendingScan.scan_type}</p>
                </div>
                <div>
                  <p className="text-gray-500">Project</p>
                  <p className="font-medium">{previewPendingScan.project_name || 'Unnamed'}</p>
                </div>
                <div>
                  <p className="text-gray-500">Status</p>
                  <Badge variant="secondary" className={
                    previewPendingScan.status === 'failed' ? 'bg-red-100 text-red-700' : 'bg-amber-100 text-amber-700'
                  }>
                    {previewPendingScan.status === 'failed' ? 'Failed' : 'Pending'}
                  </Badge>
                </div>
              </div>
              
              {/* Geolocation info */}
              {previewPendingScan.geolocation && (
                <div className="flex items-center gap-2 p-3 bg-green-50 rounded-lg">
                  <MapPin className="w-5 h-5 text-green-600" />
                  <div>
                    <p className="text-sm font-medium text-green-800">GPS Location Attached</p>
                    <p className="text-xs text-green-600">
                      {previewPendingScan.geolocation.latitude.toFixed(6)}, {previewPendingScan.geolocation.longitude.toFixed(6)}
                      {previewPendingScan.geolocation.accuracy && ` (±${Math.round(previewPendingScan.geolocation.accuracy)}m)`}
                    </p>
                  </div>
                </div>
              )}
              
              {/* Voice note info */}
              {previewPendingScan.voice_note && (
                <div className="flex items-center gap-2 p-3 bg-purple-50 rounded-lg">
                  <Mic className="w-5 h-5 text-purple-600" />
                  <div className="flex-1">
                    <p className="text-sm font-medium text-purple-800">Voice Note Attached</p>
                    <p className="text-xs text-purple-600">Duration: {formatDuration(previewPendingScan.voice_note.duration)}</p>
                  </div>
                  <audio 
                    src={previewPendingScan.voice_note.audio_base64} 
                    controls 
                    className="h-8"
                  />
                </div>
              )}
            </div>
          )}
          
          <DialogFooter className="gap-2">
            {previewPendingScan?.status === 'failed' && isOnline && (
              <Button onClick={() => { retryFailedScan(previewPendingScan.id); setPreviewPendingScan(null); }}>
                <RotateCcw className="w-4 h-4 mr-2" />
                Retry
              </Button>
            )}
            <Button variant="destructive" onClick={() => { deletePendingScan(previewPendingScan?.id); setPreviewPendingScan(null); }}>
              <Trash2 className="w-4 h-4 mr-2" />
              Delete
            </Button>
            <Button variant="outline" onClick={() => setPreviewPendingScan(null)}>
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">AI Photo Hazard Scan</h1>
          <p className="text-gray-600">Upload jobsite photos for AI-powered safety analysis</p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {/* Map View Button */}
          <Button 
            variant="outline" 
            size="sm" 
            onClick={() => setShowMapView(true)}
            className="flex items-center gap-2"
            data-testid="map-view-btn"
          >
            <Map className="w-4 h-4" />
            Map
          </Button>
          
          {/* Transcription Archive Button */}
          <Button 
            variant="outline" 
            size="sm" 
            onClick={() => setShowTranscriptionArchive(true)}
            className="flex items-center gap-2"
            data-testid="transcription-archive-btn"
          >
            <Search className="w-4 h-4" />
            Transcriptions
          </Button>
          
          {/* Trend Analysis Button */}
          <Button 
            variant="outline" 
            size="sm" 
            onClick={() => setShowTrendAnalysis(true)}
            className="flex items-center gap-2 bg-purple-50 hover:bg-purple-100 border-purple-200"
            data-testid="trend-analysis-btn"
          >
            <BarChart3 className="w-4 h-4 text-purple-600" />
            Trends
          </Button>
          
          {/* Online/Offline indicator */}
          <div className={`flex items-center gap-2 px-3 py-1 rounded-full text-sm ${
            isOnline ? 'bg-green-100 text-green-700' : 'bg-amber-100 text-amber-700'
          }`}>
            {isOnline ? <Wifi className="w-4 h-4" /> : <WifiOff className="w-4 h-4" />}
            {isOnline ? 'Online' : 'Offline'}
          </div>
        </div>
      </div>

      {/* Map View Modal */}
      {showMapView && (
        <Dialog open={showMapView} onOpenChange={setShowMapView}>
          <DialogContent className="max-w-6xl h-[80vh] p-0">
            <ScanMapView onClose={() => setShowMapView(false)} />
          </DialogContent>
        </Dialog>
      )}

      {/* Transcription Archive Modal */}
      {showTranscriptionArchive && (
        <Dialog open={showTranscriptionArchive} onOpenChange={setShowTranscriptionArchive}>
          <DialogContent className="max-w-4xl h-[80vh] p-0">
            <TranscriptionArchive onClose={() => setShowTranscriptionArchive(false)} />
          </DialogContent>
        </Dialog>
      )}

      {/* Trend Analysis Modal */}
      {showTrendAnalysis && (
        <Dialog open={showTrendAnalysis} onOpenChange={setShowTrendAnalysis}>
          <DialogContent className="max-w-5xl h-[85vh] p-0">
            <HazardTrendAnalysis onClose={() => setShowTrendAnalysis(false)} />
          </DialogContent>
        </Dialog>
      )}

      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="pt-6">
            <div className="text-center">
              <p className="text-3xl font-bold text-blue-600">{stats?.total_scans || 0}</p>
              <p className="text-sm text-gray-600">Total Scans</p>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="text-center">
              <p className="text-3xl font-bold text-orange-600">{stats?.total_hazards_found || 0}</p>
              <p className="text-sm text-gray-600">Hazards Found</p>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="text-center">
              <p className="text-3xl font-bold text-purple-600">{stats?.avg_hazards_per_scan || 0}</p>
              <p className="text-sm text-gray-600">Avg/Scan</p>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="text-center">
              <Scan className="w-8 h-8 text-green-600 mx-auto" />
              <p className="text-sm text-gray-600 mt-2">AI Powered</p>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Upload Section */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="flex items-center">
                <Camera className="w-5 h-5 mr-2 text-blue-600" />
                Scan New Photo{batchMode && 's'}
              </CardTitle>
              <CardDescription>
                {batchMode 
                  ? `${imageQueue.length} image(s) in queue for batch analysis`
                  : 'Upload jobsite photo(s) for AI safety analysis - supports multiple files'
                }
              </CardDescription>
            </div>
            {batchMode && (
              <div className="flex gap-2">
                <Badge variant="secondary" className="bg-blue-100 text-blue-700">
                  Batch Mode
                </Badge>
                <Button variant="ghost" size="sm" onClick={clearQueue} disabled={batchScanning}>
                  <X className="w-4 h-4 mr-1" />
                  Clear All
                </Button>
              </div>
            )}
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Image Upload */}
            <div>
              {/* Hidden file inputs - one for camera, one for file browser (now with multiple) */}
              <input 
                type="file" 
                accept="image/*" 
                multiple
                ref={fileInputRef} 
                onChange={handleImageSelect} 
                className="hidden"
                data-testid="file-upload-input"
              />
              <input 
                type="file" 
                accept="image/*" 
                capture="environment"
                ref={cameraInputRef} 
                onChange={handleImageSelect} 
                className="hidden"
                data-testid="camera-capture-input"
              />
              
              {!previewImage && !batchMode ? (
                <div 
                  ref={dropZoneRef}
                  onDragEnter={handleDragEnter}
                  onDragLeave={handleDragLeave}
                  onDragOver={handleDragOver}
                  onDrop={handleDrop}
                  className={`border-2 border-dashed rounded-lg p-6 text-center transition-all duration-200 ${
                    isDragging 
                      ? 'border-blue-500 bg-blue-50 scale-[1.02]' 
                      : 'border-gray-300 hover:border-gray-400'
                  }`}
                  data-testid="drop-zone"
                >
                  <div className="flex flex-col items-center mb-4">
                    <div className={`w-16 h-16 rounded-full flex items-center justify-center mb-3 transition-colors ${
                      isDragging ? 'bg-blue-200' : 'bg-blue-100'
                    }`}>
                      {isDragging ? (
                        <Download className="w-8 h-8 text-blue-600 animate-bounce" />
                      ) : (
                        <Scan className="w-8 h-8 text-blue-600" />
                      )}
                    </div>
                    {isDragging ? (
                      <>
                        <p className="text-blue-600 font-semibold mb-1">Drop your image here!</p>
                        <p className="text-sm text-blue-500">Release to upload</p>
                      </>
                    ) : (
                      <>
                        <p className="text-gray-700 font-medium mb-1">Upload an Image for Analysis</p>
                        <p className="text-sm text-gray-500">Drag & drop or use buttons below</p>
                        <p className="text-xs text-gray-400 mt-1">PNG, JPG, HEIC up to 10MB</p>
                      </>
                    )}
                  </div>
                  
                  {!isDragging && (
                    <>
                      <div className="flex flex-col sm:flex-row gap-3 justify-center">
                        <Button 
                          variant="default"
                          className="bg-blue-600 hover:bg-blue-700 flex-1 sm:flex-none"
                          onClick={() => fileInputRef.current?.click()}
                          data-testid="upload-from-files-btn"
                        >
                          <Upload className="w-4 h-4 mr-2" />
                          Upload from Files
                        </Button>
                        <Button 
                          variant="outline"
                          className="flex-1 sm:flex-none"
                          onClick={() => cameraInputRef.current?.click()}
                          data-testid="take-photo-btn"
                        >
                          <Camera className="w-4 h-4 mr-2" />
                          Take Photo
                        </Button>
                      </div>
                      
                      <p className="text-xs text-gray-400 mt-4">
                        Supported: Construction sites, warehouses, factories, offices
                      </p>
                    </>
                  )}
                </div>
              ) : batchMode ? (
                /* Batch Queue UI */
                <div className="space-y-4">
                  {/* Drop zone for adding more */}
                  <div 
                    ref={dropZoneRef}
                    onDragEnter={handleDragEnter}
                    onDragLeave={handleDragLeave}
                    onDragOver={handleDragOver}
                    onDrop={handleDrop}
                    className={`border-2 border-dashed rounded-lg p-4 text-center transition-all ${
                      isDragging 
                        ? 'border-blue-500 bg-blue-50' 
                        : 'border-gray-200 hover:border-gray-300'
                    }`}
                  >
                    <div className="flex items-center justify-center gap-3">
                      <Upload className="w-5 h-5 text-gray-400" />
                      <span className="text-sm text-gray-500">
                        {isDragging ? 'Drop to add more images' : 'Drag more images here or'}
                      </span>
                      {!isDragging && (
                        <Button 
                          variant="outline" 
                          size="sm"
                          onClick={() => fileInputRef.current?.click()}
                        >
                          Browse Files
                        </Button>
                      )}
                    </div>
                  </div>
                  
                  {/* Image Queue */}
                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 max-h-64 overflow-y-auto p-1">
                    {imageQueue.map((item, index) => (
                      <div 
                        key={item.id}
                        className={`relative rounded-lg overflow-hidden border-2 transition-all ${
                          item.status === 'scanning' ? 'border-blue-500 ring-2 ring-blue-200' :
                          item.status === 'complete' ? 'border-green-500' :
                          item.status === 'error' ? 'border-red-500' :
                          'border-gray-200'
                        }`}
                      >
                        <img 
                          src={item.preview} 
                          alt={`Queue ${index + 1}`}
                          className="w-full h-24 object-cover"
                        />
                        
                        {/* Status overlay */}
                        <div className={`absolute inset-0 flex items-center justify-center ${
                          item.status === 'scanning' ? 'bg-blue-500/30' :
                          item.status === 'complete' ? 'bg-green-500/20' :
                          item.status === 'error' ? 'bg-red-500/20' :
                          ''
                        }`}>
                          {item.status === 'scanning' && (
                            <RefreshCw className="w-6 h-6 text-blue-600 animate-spin" />
                          )}
                          {item.status === 'complete' && (
                            <CheckCircle className="w-6 h-6 text-green-600" />
                          )}
                          {item.status === 'error' && (
                            <AlertTriangle className="w-6 h-6 text-red-600" />
                          )}
                        </div>
                        
                        {/* Remove button (only for pending) */}
                        {item.status === 'pending' && !batchScanning && (
                          <button
                            onClick={() => removeFromQueue(item.id)}
                            className="absolute top-1 right-1 w-5 h-5 bg-red-500 hover:bg-red-600 text-white rounded-full flex items-center justify-center text-xs"
                          >
                            <X className="w-3 h-3" />
                          </button>
                        )}
                        
                        {/* Index badge */}
                        <div className="absolute bottom-1 left-1 bg-black/60 text-white text-xs px-1.5 py-0.5 rounded">
                          {index + 1}
                        </div>
                        
                        {/* Result badge */}
                        {item.status === 'complete' && item.result?.analysis?.hazards_found && (
                          <div className="absolute bottom-1 right-1 bg-orange-500 text-white text-xs px-1.5 py-0.5 rounded">
                            {item.result.analysis.hazards_found.length} hazards
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                  
                  {/* Batch progress */}
                  {batchScanning && (
                    <div className="space-y-2">
                      <div className="flex justify-between text-sm text-gray-600">
                        <span>Analyzing images...</span>
                        <span>{currentBatchIndex + 1} of {imageQueue.length}</span>
                      </div>
                      <Progress value={((currentBatchIndex + 1) / imageQueue.length) * 100} />
                    </div>
                  )}
                  
                  {/* Batch Export Options - shown when all scans complete */}
                  {!batchScanning && imageQueue.some(i => i.status === 'complete') && (
                    <div className="p-4 bg-green-50 border border-green-200 rounded-lg">
                      <div className="flex items-center justify-between mb-3">
                        <div className="flex items-center gap-2">
                          <CheckCircle className="w-5 h-5 text-green-600" />
                          <span className="font-medium text-green-800">
                            {imageQueue.filter(i => i.status === 'complete').length} scans completed
                          </span>
                        </div>
                        <Badge className="bg-green-600">
                          {imageQueue.reduce((sum, i) => sum + (i.result?.analysis?.hazards_found?.length || 0), 0)} total hazards
                        </Badge>
                      </div>
                      <p className="text-sm text-green-700 mb-3">
                        Export a combined report with all hazard findings
                      </p>
                      <div className="flex flex-wrap gap-2">
                        <Button 
                          size="sm" 
                          variant="outline"
                          className="bg-white border-green-300 text-green-700 hover:bg-green-100"
                          onClick={() => handleBatchExport('html')}
                          data-testid="batch-export-html-btn"
                        >
                          <FileText className="w-3 h-3 mr-1" />
                          Export HTML
                        </Button>
                        <Button 
                          size="sm" 
                          variant="outline"
                          className="bg-white border-green-300 text-green-700 hover:bg-green-100"
                          onClick={() => handleBatchExport('txt')}
                          data-testid="batch-export-txt-btn"
                        >
                          <FileText className="w-3 h-3 mr-1" />
                          Export TXT
                        </Button>
                        <Button 
                          size="sm" 
                          variant="outline"
                          className="bg-white border-green-300 text-green-700 hover:bg-green-100"
                          onClick={() => handleBatchExport('json')}
                          data-testid="batch-export-json-btn"
                        >
                          <Download className="w-3 h-3 mr-1" />
                          Export JSON
                        </Button>
                      </div>
                    </div>
                  )}
                </div>
              ) : (
                <div className="relative">
                  <img src={previewImage} alt="Preview" className="w-full h-64 object-cover rounded-lg border" />
                  <div className="absolute top-2 right-2 flex gap-2">
                    <Button 
                      variant="outline" 
                      size="sm" 
                      className="bg-white shadow-sm"
                      onClick={() => fileInputRef.current?.click()}
                      data-testid="change-image-btn"
                    >
                      <Upload className="w-3 h-3 mr-1" />
                      Change
                    </Button>
                    <Button 
                      variant="outline" 
                      size="sm" 
                      className="bg-white shadow-sm"
                      onClick={() => { setPreviewImage(null); setImageBase64(null); }}
                      data-testid="remove-image-btn"
                    >
                      <X className="w-3 h-3" />
                    </Button>
                  </div>
                  <div className="absolute bottom-2 left-2 bg-green-500 text-white text-xs px-2 py-1 rounded-full flex items-center">
                    <CheckCircle className="w-3 h-3 mr-1" />
                    Image ready
                  </div>
                </div>
              )}
            </div>

            {/* Scan Options */}
            <div className="space-y-4">
              <div>
                <Label>Scan Type</Label>
                <Select value={scanConfig.scan_type} onValueChange={(v) => setScanConfig({...scanConfig, scan_type: v})}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {scanTypes.map((type) => (
                      <SelectItem key={type.value} value={type.value}>
                        {type.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <p className="text-xs text-gray-500 mt-1">
                  {scanTypes.find(t => t.value === scanConfig.scan_type)?.description}
                </p>
              </div>

              <div>
                <Label>Location</Label>
                <Input 
                  value={scanConfig.location} 
                  onChange={(e) => setScanConfig({...scanConfig, location: e.target.value})} 
                  placeholder={batchMode ? "Location for all images" : "Where was this photo taken?"}
                />
              </div>

              <div>
                <Label>Project Name</Label>
                <Input 
                  value={scanConfig.project_name} 
                  onChange={(e) => setScanConfig({...scanConfig, project_name: e.target.value})} 
                  placeholder="Project or site name"
                />
              </div>

              {/* Geolocation Section */}
              <div className="border rounded-lg p-3 bg-gray-50">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <MapPin className={`w-5 h-5 ${geoLocation ? 'text-green-600' : 'text-gray-400'}`} />
                    <div>
                      <p className="text-sm font-medium">GPS Location</p>
                      {geoLocation ? (
                        <p className="text-xs text-green-600">
                          {geoLocation.latitude.toFixed(5)}, {geoLocation.longitude.toFixed(5)}
                        </p>
                      ) : geoError ? (
                        <p className="text-xs text-red-500">{geoError}</p>
                      ) : (
                        <p className="text-xs text-gray-500">Not captured</p>
                      )}
                    </div>
                  </div>
                  <Button 
                    type="button"
                    size="sm"
                    variant={geoLocation ? "outline" : "default"}
                    onClick={getCurrentLocation}
                    disabled={geoLoading}
                    className={geoLocation ? "text-green-600 border-green-300" : ""}
                    data-testid="get-location-btn"
                  >
                    {geoLoading ? (
                      <RefreshCw className="w-4 h-4 animate-spin" />
                    ) : geoLocation ? (
                      <>
                        <CheckCircle className="w-4 h-4 mr-1" />
                        Captured
                      </>
                    ) : (
                      <>
                        <MapPin className="w-4 h-4 mr-1" />
                        Get Location
                      </>
                    )}
                  </Button>
                </div>
              </div>

              {/* Voice Note Section */}
              <div className="border rounded-lg p-3 bg-gray-50">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Mic className={`w-5 h-5 ${voiceNote ? 'text-purple-600' : isRecording ? 'text-red-600 animate-pulse' : 'text-gray-400'}`} />
                    <div>
                      <p className="text-sm font-medium">Voice Note</p>
                      {isRecording ? (
                        <p className="text-xs text-red-600">Recording... {formatDuration(voiceDuration)}</p>
                      ) : voiceNote ? (
                        <p className="text-xs text-purple-600">Recorded ({formatDuration(voiceNote.duration)})</p>
                      ) : (
                        <p className="text-xs text-gray-500">Optional audio description</p>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    {voiceNote && !isRecording && (
                      <>
                        <Button
                          type="button"
                          size="sm"
                          variant="ghost"
                          onClick={isPlayingVoice ? stopPlayingVoice : playVoiceNote}
                          className="text-purple-600"
                        >
                          {isPlayingVoice ? <Pause className="w-4 h-4" /> : <Play className="w-4 h-4" />}
                        </Button>
                        <Button
                          type="button"
                          size="sm"
                          variant="ghost"
                          onClick={transcribeVoiceNote}
                          disabled={isTranscribing}
                          className="text-blue-600"
                          title="Transcribe to text"
                          data-testid="transcribe-btn"
                        >
                          {isTranscribing ? (
                            <RefreshCw className="w-4 h-4 animate-spin" />
                          ) : (
                            <FileAudio className="w-4 h-4" />
                          )}
                        </Button>
                        <Button
                          type="button"
                          size="sm"
                          variant="ghost"
                          onClick={deleteVoiceNote}
                          className="text-red-600"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </>
                    )}
                    <Button 
                      type="button"
                      size="sm"
                      variant={isRecording ? "destructive" : voiceNote ? "outline" : "default"}
                      onClick={isRecording ? stopRecording : startRecording}
                      className={voiceNote && !isRecording ? "text-purple-600 border-purple-300" : ""}
                      data-testid="voice-record-btn"
                    >
                      {isRecording ? (
                        <>
                          <MicOff className="w-4 h-4 mr-1" />
                          Stop
                        </>
                      ) : voiceNote ? (
                        <>
                          <Mic className="w-4 h-4 mr-1" />
                          Re-record
                        </>
                      ) : (
                        <>
                          <Mic className="w-4 h-4 mr-1" />
                          Record
                        </>
                      )}
                    </Button>
                  </div>
                </div>
                
                {/* Voice Transcription Display */}
                {voiceTranscription && (
                  <div className="mt-3 p-3 bg-blue-50 rounded-lg border border-blue-200">
                    <div className="flex items-center gap-2 mb-1">
                      <FileAudio className="w-4 h-4 text-blue-600" />
                      <span className="text-xs font-medium text-blue-700">Transcription</span>
                    </div>
                    <p className="text-sm text-gray-700">{voiceTranscription}</p>
                  </div>
                )}
                
                {/* Hidden audio player for playback */}
                <audio 
                  ref={audioPlayerRef} 
                  onEnded={() => setIsPlayingVoice(false)}
                  className="hidden"
                />
              </div>

              {batchMode ? (
                <Button 
                  className="w-full bg-purple-600 hover:bg-purple-700" 
                  onClick={processBatchScan}
                  disabled={imageQueue.length === 0 || batchScanning || imageQueue.every(i => i.status !== 'pending')}
                  data-testid="batch-scan-btn"
                >
                  {batchScanning ? (
                    <>
                      <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                      Analyzing {currentBatchIndex + 1} of {imageQueue.length}...
                    </>
                  ) : (
                    <>
                      <Zap className="w-4 h-4 mr-2" />
                      Scan All {imageQueue.filter(i => i.status === 'pending').length} Images
                    </>
                  )}
                </Button>
              ) : (
                <Button 
                  className="w-full bg-blue-600 hover:bg-blue-700" 
                  onClick={handleScan}
                  disabled={!imageBase64 || scanning}
                  data-testid="single-scan-btn"
                >
                  {scanning ? (
                    <>
                      <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                      Analyzing...
                    </>
                  ) : (
                    <>
                      <Scan className="w-4 h-4 mr-2" />
                      Scan for Hazards
                    </>
                  )}
                </Button>
              )}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Previous Scans */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center">
            <FileText className="w-5 h-5 mr-2 text-gray-600" />
            Previous Scans
          </CardTitle>
        </CardHeader>
        <CardContent>
          {scans.length > 0 ? (
            <div className="space-y-3">
              {scans.map((scan) => (
                <div 
                  key={scan.id} 
                  className="flex items-center justify-between p-4 bg-gray-50 rounded-lg hover:bg-gray-100 cursor-pointer transition-colors"
                  onClick={() => setSelectedScan(scan)}
                >
                  <div className="flex items-center gap-4">
                    {/* Thumbnail or Risk Icon */}
                    {scan.image_url ? (
                      <div className="w-12 h-12 rounded-lg overflow-hidden border flex-shrink-0">
                        <img 
                          src={scan.image_url} 
                          alt="Scan thumbnail" 
                          className="w-full h-full object-cover"
                        />
                      </div>
                    ) : (
                      <div className={`w-10 h-10 rounded-full flex items-center justify-center ${getRiskColor(scan.overall_risk)}`}>
                        {getSeverityIcon(scan.overall_risk)}
                      </div>
                    )}
                    <div>
                      <p className="font-medium">{scan.scan_number}</p>
                      <p className="text-sm text-gray-600">
                        {scan.location || 'No location'} • {scan.scan_type?.replace('_', ' ')}
                      </p>
                    </div>
                  </div>
                  <div className="text-right">
                    <Badge className={getRiskColor(scan.overall_risk)}>
                      {scan.overall_risk} risk
                    </Badge>
                    <p className="text-xs text-gray-500 mt-1">
                      {scan.hazards_count} hazards • {new Date(scan.created_at).toLocaleDateString()}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-8">
              <Scan className="w-12 h-12 text-gray-400 mx-auto mb-4" />
              <p className="text-gray-600">No scans yet. Upload a photo to get started!</p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Scan Results Dialog */}
      <Dialog open={!!selectedScan} onOpenChange={() => setSelectedScan(null)}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          {selectedScan && (
            <>
              <DialogHeader>
                <DialogTitle className="flex items-center justify-between">
                  <span>Scan Results</span>
                  <Badge className={getRiskColor(selectedScan.overall_risk || selectedScan.analysis?.overall_risk_level)}>
                    {selectedScan.overall_risk || selectedScan.analysis?.overall_risk_level} Risk
                  </Badge>
                </DialogTitle>
                <DialogDescription>
                  {selectedScan.scan_number || 'New Scan'} • {selectedScan.location || scanConfig.location || 'Unknown location'}
                </DialogDescription>
              </DialogHeader>

              {/* Share & Download Actions */}
              <div className="flex items-center gap-2 pb-2 border-b flex-wrap">
                <Button 
                  variant="outline" 
                  size="sm" 
                  onClick={() => handleDownload('html')}
                  className="flex-1 min-w-[120px]"
                  data-testid="download-report-btn"
                >
                  <Download className="w-4 h-4 mr-2" />
                  Download
                </Button>
                <Button 
                  variant="outline" 
                  size="sm" 
                  onClick={handleCopyToClipboard}
                  className="flex-1 min-w-[120px]"
                  data-testid="copy-report-btn"
                >
                  {copied ? (
                    <>
                      <ClipboardCheck className="w-4 h-4 mr-2 text-green-600" />
                      Copied!
                    </>
                  ) : (
                    <>
                      <Copy className="w-4 h-4 mr-2" />
                      Copy
                    </>
                  )}
                </Button>
                <Button 
                  variant="outline" 
                  size="sm" 
                  onClick={() => setShowEmailDialog(true)}
                  className="flex-1 min-w-[100px]"
                  data-testid="email-report-btn"
                >
                  <Mail className="w-4 h-4 mr-2" />
                  Email
                </Button>
                <Button 
                  variant="outline" 
                  size="sm" 
                  onClick={handleGenerateShareLink}
                  disabled={generatingLink}
                  className="flex-1 min-w-[100px]"
                  data-testid="share-link-btn"
                >
                  {generatingLink ? (
                    <>
                      <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                      Creating...
                    </>
                  ) : (
                    <>
                      <Link className="w-4 h-4 mr-2" />
                      Share
                    </>
                  )}
                </Button>
                <Select defaultValue="html" onValueChange={(format) => handleDownload(format)}>
                  <SelectTrigger className="w-20">
                    <SelectValue placeholder="Format" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="html">HTML</SelectItem>
                    <SelectItem value="txt">Text</SelectItem>
                    <SelectItem value="json">JSON</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-4">
                {/* Scanned Image */}
                <Card>
                  <CardContent className="p-4">
                    <h4 className="font-medium mb-2 flex items-center">
                      <Camera className="w-4 h-4 mr-2 text-blue-500" />
                      Scanned Image
                    </h4>
                    {selectedScan.image_url ? (
                      <div className="rounded-lg overflow-hidden border">
                        <img 
                          src={selectedScan.image_url} 
                          alt="Scanned site" 
                          className="w-full h-auto max-h-64 object-contain bg-gray-100"
                        />
                      </div>
                    ) : (
                      <div className="rounded-lg border border-dashed border-gray-300 bg-gray-50 p-8 text-center">
                        <Camera className="w-12 h-12 text-gray-300 mx-auto mb-2" />
                        <p className="text-sm text-gray-500">
                          Image not available for this scan
                        </p>
                        <p className="text-xs text-gray-400 mt-1">
                          Images are stored for scans created after Feb 7, 2026
                        </p>
                      </div>
                    )}
                  </CardContent>
                </Card>

                {/* Summary */}
                {selectedScan.analysis?.summary && (
                  <Card>
                    <CardContent className="p-4">
                      <h4 className="font-medium mb-2">Summary</h4>
                      <p className="text-gray-600">{selectedScan.analysis.summary}</p>
                    </CardContent>
                  </Card>
                )}

                {/* Hazards Found */}
                {selectedScan.analysis?.hazards_found?.length > 0 && (
                  <div>
                    <h4 className="font-medium mb-3 flex items-center">
                      <AlertTriangle className="w-4 h-4 mr-2 text-orange-500" />
                      Hazards Found ({selectedScan.analysis.hazards_found.length})
                    </h4>
                    <div className="space-y-3">
                      {selectedScan.analysis.hazards_found.map((hazard, index) => (
                        <Card key={index} className={`border-l-4 ${getRiskColor(hazard.severity)}`}>
                          <CardContent className="p-4">
                            <div className="flex items-start justify-between">
                              <div>
                                <p className="font-medium">{hazard.hazard_type}</p>
                                <p className="text-sm text-gray-600 mt-1">{hazard.description}</p>
                                {hazard.location_in_image && (
                                  <p className="text-xs text-gray-500 mt-1">Location: {hazard.location_in_image}</p>
                                )}
                              </div>
                              <Badge className={getRiskColor(hazard.severity)}>{hazard.severity}</Badge>
                            </div>
                            {hazard.recommended_action && (
                              <div className="mt-3 p-2 bg-blue-50 rounded">
                                <p className="text-sm text-blue-800">
                                  <strong>Action:</strong> {hazard.recommended_action}
                                </p>
                              </div>
                            )}
                            {hazard.osha_reference && (
                              <p className="text-xs text-gray-500 mt-2">OSHA: {hazard.osha_reference}</p>
                            )}
                            {(selectedScan.id || selectedScan.scan_id) && (
                              <WsafeReviewStatus sourceType="ai_hazard_scan" sourceId={`${selectedScan.id || selectedScan.scan_id}:${index}`} />
                            )}
                          </CardContent>
                        </Card>
                      ))}
                    </div>
                  </div>
                )}

                {/* Immediate Actions */}
                {selectedScan.analysis?.immediate_actions_required?.length > 0 && (
                  <Card className="border-red-200 bg-red-50">
                    <CardContent className="p-4">
                      <h4 className="font-medium text-red-800 mb-2">Immediate Actions Required</h4>
                      <ul className="space-y-1">
                        {selectedScan.analysis.immediate_actions_required.map((action, index) => (
                          <li key={index} className="text-sm text-red-700 flex items-start">
                            <span className="mr-2">•</span>{action}
                          </li>
                        ))}
                      </ul>
                    </CardContent>
                  </Card>
                )}

                {/* Positive Observations */}
                {selectedScan.analysis?.positive_observations?.length > 0 && (
                  <Card className="border-green-200 bg-green-50">
                    <CardContent className="p-4">
                      <h4 className="font-medium text-green-800 mb-2 flex items-center">
                        <CheckCircle className="w-4 h-4 mr-2" /> Positive Observations
                      </h4>
                      <ul className="space-y-1">
                        {selectedScan.analysis.positive_observations.map((obs, index) => (
                          <li key={index} className="text-sm text-green-700 flex items-start">
                            <span className="mr-2">✓</span>{obs}
                          </li>
                        ))}
                      </ul>
                    </CardContent>
                  </Card>
                )}

                {/* Auto-created Reports */}
                {selectedScan.auto_reports_created?.length > 0 && (
                  <Card className="border-blue-200 bg-blue-50">
                    <CardContent className="p-4">
                      <h4 className="font-medium text-blue-800 mb-2">Auto-Created Hazard Reports</h4>
                      <p className="text-sm text-blue-700">
                        Critical/High severity hazards have been automatically logged:
                      </p>
                      <div className="flex flex-wrap gap-2 mt-2">
                        {selectedScan.auto_reports_created.map((report, index) => (
                          <Badge key={index} variant="outline" className="bg-white">{report}</Badge>
                        ))}
                      </div>
                    </CardContent>
                  </Card>
                )}

                {selectedScan.analysis?.is_mock && (
                  <p className="text-xs text-gray-500 text-center italic">
                    Note: This is sample data. Configure AI API key for real analysis.
                  </p>
                )}
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Email Dialog */}
      <Dialog open={showEmailDialog} onOpenChange={setShowEmailDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center">
              <Mail className="w-5 h-5 mr-2 text-blue-600" />
              Share Scan Report via Email
            </DialogTitle>
            <DialogDescription>
              Send this hazard scan report to team members or stakeholders
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div>
              <Label>Recipients (comma-separated emails)</Label>
              <Input
                placeholder="john@example.com, jane@example.com"
                value={emailData.recipients}
                onChange={(e) => setEmailData({...emailData, recipients: e.target.value})}
              />
            </div>
            
            <div>
              <Label>Personal Message (optional)</Label>
              <Textarea
                placeholder="Please review this hazard scan report for our site inspection..."
                value={emailData.message}
                onChange={(e) => setEmailData({...emailData, message: e.target.value})}
                rows={3}
              />
            </div>
            
            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                id="includeImage"
                checked={emailData.includeImage}
                onChange={(e) => setEmailData({...emailData, includeImage: e.target.checked})}
                className="rounded border-gray-300"
              />
              <Label htmlFor="includeImage" className="cursor-pointer">
                Include scanned image in email
              </Label>
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowEmailDialog(false)}>
              Cancel
            </Button>
            <Button onClick={handleSendEmail} disabled={sendingEmail || !emailData.recipients.trim()}>
              {sendingEmail ? (
                <>
                  <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                  Sending...
                </>
              ) : (
                <>
                  <Mail className="w-4 h-4 mr-2" />
                  Send Report
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Share Link Dialog */}
      <Dialog open={showShareDialog} onOpenChange={setShowShareDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center">
              <Link className="w-5 h-5 mr-2 text-blue-600" />
              Share Scan Report
            </DialogTitle>
            <DialogDescription>
              Anyone with this link can view the scan report
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            {shareLink ? (
              <>
                {/* QR Code Section */}
                <div className="flex justify-center">
                  <div className="bg-white p-4 rounded-xl border-2 border-gray-100 shadow-sm">
                    <QRCodeSVG 
                      value={shareLink.url}
                      size={160}
                      level="M"
                      includeMargin={true}
                      bgColor="#ffffff"
                      fgColor="#1e40af"
                      imageSettings={{
                        src: "",
                        height: 0,
                        width: 0,
                        excavate: false,
                      }}
                    />
                  </div>
                </div>
                
                <p className="text-center text-sm text-gray-500 flex items-center justify-center gap-1">
                  <QrCode className="w-4 h-4" />
                  Scan QR code to view report on mobile
                </p>

                <div className="bg-gray-50 rounded-lg p-4 border">
                  <Label className="text-xs text-gray-500 mb-2 block">Or copy link</Label>
                  <div className="flex items-center gap-2">
                    <Input
                      value={shareLink.url}
                      readOnly
                      className="flex-1 font-mono text-xs bg-white"
                      data-testid="share-link-input"
                    />
                    <Button 
                      size="sm" 
                      onClick={handleCopyShareLink}
                      variant={linkCopied ? "default" : "outline"}
                      data-testid="copy-share-link-btn"
                    >
                      {linkCopied ? (
                        <>
                          <ClipboardCheck className="w-4 h-4 mr-1" />
                          Copied!
                        </>
                      ) : (
                        <>
                          <Copy className="w-4 h-4 mr-1" />
                          Copy
                        </>
                      )}
                    </Button>
                  </div>
                </div>
                
                <div className="flex items-center justify-between text-sm text-gray-500">
                  <span className="flex items-center gap-1">
                    <Clock className="w-4 h-4" />
                    Expires: {shareLink.expiresAt ? new Date(shareLink.expiresAt).toLocaleDateString() : 'Never'}
                  </span>
                  <a 
                    href={shareLink.url} 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="flex items-center gap-1 text-blue-600 hover:underline"
                  >
                    <ExternalLink className="w-4 h-4" />
                    Preview
                  </a>
                </div>

                <div className="bg-blue-50 rounded-lg p-3 border border-blue-200">
                  <p className="text-sm text-blue-700 flex items-start gap-2">
                    <Info className="w-4 h-4 mt-0.5 flex-shrink-0" />
                    This link allows anyone to view the scan report without logging in. Share responsibly.
                  </p>
                </div>
              </>
            ) : (
              <div className="text-center py-8 text-gray-500">
                <RefreshCw className="w-8 h-8 mx-auto mb-2 animate-spin" />
                Generating share link...
              </div>
            )}
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowShareDialog(false)}>
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AIHazardScanPage;
